import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Stack,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Tooltip
} from '@mui/material';
import {
    Print as PrintIcon,
    Close as CloseIcon,
    Download as DownloadIcon,
    Email as EmailIcon,
    NotificationsActive as ReminderIcon,
    ArrowBack as ArrowBackIcon,
    NavigateNext as NavigateNextIcon,
    Home as HomeIcon,
    Receipt as InvoiceIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import DashboardLayout from '../../../components/layouts/DashboardLayout';
import { invoiceService } from '../../../services/invoiceService';
import pdfService from '../../../services/pdfService';
// Loaded on demand: it pulls in the rich text editor, which is a large dependency
const EmailInvoiceDialog = lazy(() => import('./EmailInvoiceDialog'));

const InvoiceDetails = ({ invoice, onClose, userType = 'employee' }) => {
    const { id } = useParams(); // For standalone page usage
    const navigate = useNavigate();
    const location = useLocation();
    const { showError, showSuccess, handleApiCallWithLoading } = useApiHandler();
    const [loading, setLoading] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    const [emailDialog, setEmailDialog] = useState({ open: false, type: 'invoice' });

    // Determine if this is standalone page or modal usage
    const isStandalonePage = !invoice && id;
    const invoiceId = invoice?.invoice_id || id;

    // Custom breadcrumb for invoice pages
    const getBreadcrumbItems = () => {
        if (!isStandalonePage) return [];

        const basePath = userType === 'customer' ? '/customer' : '/employee';

        const items = [
            {
                text: 'Dashboard',
                path: `${basePath}/dashboard`,
                icon: <HomeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            },
            {
                text: userType === 'customer' ? 'My Invoices' : 'Invoice Management',
                path: `${basePath}/invoices`,
                icon: <InvoiceIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            }
        ];

        if (invoiceDetails?.invoice_info?.invoice_no) {
            items.push({
                text: `Invoice ${invoiceDetails.invoice_info.invoice_no}`,
                path: `${basePath}/invoices/${invoiceId}`,
                icon: null
            });
        }

        return items;
    };

    useEffect(() => {
        if (invoiceId) {
            loadInvoiceDetails();
        }
    }, [invoiceId]);

    const loadInvoiceDetails = async () => {
        setLoading(true);
        try {
            let response;
            // Use different service based on user type
            if (userType === 'customer') {
                const customerInvoiceService = require('../../../services/customerInvoiceService').default;
                response = await customerInvoiceService.getInvoiceDetails(invoiceId);
            } else {
                response = await invoiceService.getInvoiceById(invoiceId);
            }

            if (response.success) {
                setInvoiceDetails(response.data);
            } else {
                showError('Failed to load invoice details');
            }
        } catch (error) {
            console.error('Error loading invoice:', error);
            if (error.response?.status === 404) {
                showError('Invoice not found or you do not have permission to view it');
            } else {
                showError('Failed to load invoice details');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        try {
            const result = await pdfService.generateAndPrintProfessionalInvoice(invoiceDetails);
            if (result.success) {
                showSuccess('Invoice opened for printing');
            } else {
                showError(result.message);
            }
        } catch (error) {
            console.error('Error printing invoice:', error);
            showError('Error generating PDF for print: ' + error.message);
        }
    };

    const handleDownload = async () => {
        try {
            const result = await pdfService.generateAndDownloadLegacyInvoice(invoiceDetails);
            if (result.success) {
                showSuccess('PDF downloaded successfully');
            } else {
                showError(result.message);
            }
        } catch (error) {
            console.error('Error downloading PDF:', error);
            showError('Error generating PDF: ' + error.message);
        }
    };

    const handleEmail = () => {
        setEmailDialog({ open: true, type: 'invoice' });
    };

    const handleSendReminder = () => {
        setEmailDialog({ open: true, type: 'invoice_reminder' });
    };

    const handleCloseEmailDialog = () => {
        setEmailDialog((prev) => ({ ...prev, open: false }));
    };

    const handleClose = () => {
        if (onClose) {
            onClose(); // Modal usage
        } else {
            // If on invoice details, go back to invoice list
            const basePath = userType === 'customer' ? '/customer' : '/employee';
            navigate(`${basePath}/invoices`);
        }
    };

    const handleBackToList = () => {
        const basePath = userType === 'customer' ? '/customer' : '/employee';
        navigate(`${basePath}/invoices`);
    };

    const formatCurrency = (amount) => {
        return invoiceService.formatCurrency(amount);
    };

    const formatDate = (dateString) => {
        return invoiceService.formatDate(dateString);
    };

    const getStatusColor = (status) => {
        return invoiceService.getStatusColor(status);
    };

    // Render breadcrumb component
    const renderBreadcrumb = () => {
        const breadcrumbItems = getBreadcrumbItems();
        
        if (breadcrumbItems.length === 0) return null;

        return (
            <Box sx={{ mb: 2, '@media print': { display: 'none' } }}>
                <Breadcrumbs
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                    sx={{
                        '& .MuiBreadcrumbs-separator': {
                            color: 'text.secondary',
                        },
                    }}
                >
                    {breadcrumbItems.map((item, index) => {
                        const isLast = index === breadcrumbItems.length - 1;

                        if (isLast) {
                            return (
                                <Typography
                                    key={item.path}
                                    color="text.primary"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontWeight: 'medium',
                                    }}
                                >
                                    {item.icon}
                                    {item.text}
                                </Typography>
                            );
                        }

                        return (
                            <Link
                                key={item.path}
                                color="inherit"
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(item.path);
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                {item.icon}
                                {item.text}
                            </Link>
                        );
                    })}
                </Breadcrumbs>
            </Box>
        );
    };

    if (loading) {
        const loadingContent = (
            <Box>
                {isStandalonePage && renderBreadcrumb()}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
        
        return isStandalonePage ? (
            <DashboardLayout userType={userType}>
                {loadingContent}
            </DashboardLayout>
        ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!invoiceDetails) {
        const errorContent = (
            <Box>
                {isStandalonePage && renderBreadcrumb()}
                <Box sx={{ p: 4 }}>
                    <Alert severity="error">Failed to load invoice details</Alert>
                    {isStandalonePage && (
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackToList}
                            >
                                Back to Invoice List
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>
        );
        
        return isStandalonePage ? (
            <DashboardLayout userType={userType}>
                {errorContent}
            </DashboardLayout>
        ) : (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">Failed to load invoice details</Alert>
            </Box>
        );
    }

    const { invoice_info, customer_info, summary_data, booking_details, company_info } = invoiceDetails;

    const content = (
        <Box sx={{ p: 4 }}>
            {/* Breadcrumb Navigation - only for standalone pages */}
            {isStandalonePage && renderBreadcrumb()}

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" component="h2">
                    Invoice Details
                </Typography>
                <Stack direction="row" spacing={1}>
                    {isStandalonePage && (
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBackToList}
                        >
                            Back to List
                        </Button>
                    )}
                    
                    {/* Action buttons - only show on standalone page */}
                    {isStandalonePage && (
                        <>
                    {/* Print button */}
                            <Tooltip title={invoice_info?.status === 'cancelled' ? 'Cannot print cancelled invoice' : 'Print Invoice PDF'}>
                                <span>
                    <Button
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                                        disabled={invoice_info?.status === 'cancelled'}
                    >
                                        Print PDF
                    </Button>
                                </span>
                            </Tooltip>
                            
                            <Tooltip title={invoice_info?.status === 'cancelled' ? 'Cannot download cancelled invoice' : 'Download PDF'}>
                                <span>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                                        disabled={invoice_info?.status === 'cancelled'}
                    >
                        Download PDF
                    </Button>
                                </span>
                            </Tooltip>
                            
                            {/* Emailing is a back-office action; the API rejects customer tokens */}
                            {userType !== 'customer' && (
                                <Tooltip title={invoice_info?.status === 'cancelled' ? 'Cannot email cancelled invoice' : 'Email Invoice'}>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            startIcon={<EmailIcon />}
                                            onClick={handleEmail}
                                            disabled={invoice_info?.status === 'cancelled'}
                                        >
                                            Email
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}

                            {/* Payment reminder - only meaningful while the invoice is outstanding */}
                            {userType !== 'customer' && (
                                <Tooltip title={
                                    invoice_info?.status === 'paid'
                                        ? 'Invoice is already paid'
                                        : invoice_info?.status === 'cancelled'
                                            ? 'Cannot send reminder for cancelled invoice'
                                            : 'Send payment reminder'
                                }>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            startIcon={<ReminderIcon />}
                                            onClick={handleSendReminder}
                                            disabled={['cancelled', 'paid'].includes(invoice_info?.status)}
                                        >
                                            Send Reminder
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </>
                    )}
                    
                    {/* Close button for modal usage */}
                    {!isStandalonePage && (
                        <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={handleClose}
                        >
                            Close
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Invoice Content */}
            <Box sx={{ mt: 3 }}>
                {/* Company Header with Logo */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            {/* Company Logo */}
                            <Box 
                                sx={{ 
                                    mb: 2,
                                    '& img': {
                                        height: { xs: '60px', sm: '80px' },
                                        maxWidth: { xs: '250px', sm: '350px' },
                                        width: 'auto',
                                        objectFit: 'contain',
                                        filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.1))'
                                    }
                                }}
                            >
                                <img 
                                    src="/admin/assets/images/logo.png"
                                    alt="SunEx Courier"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </Box>
                            
                            <Typography variant="h4" component="h4" color="primary" gutterBottom>
                                {company_info?.name || 'SUN-EX COURIER, CARGO & LOGISTICS PVT. LTD.'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {company_info?.head_office?.address}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {company_info?.head_office?.state}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {company_info?.head_office?.contact}
                            </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Typography variant="h5" component="h2" color="secondary">
                                INVOICE
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                {/* Invoice Info & Customer Info */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Invoice Information
                                </Typography>
                                <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Invoice No:
                                        </Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {invoice_info?.invoice_no}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Invoice Date:
                                        </Typography>
                                        <Typography variant="body2">
                                            {invoice_info?.invoice_date}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Due Date:
                                        </Typography>
                                        <Typography variant="body2">
                                            {invoice_info?.due_date}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Status:
                                        </Typography>
                                        <Chip
                                            label={invoiceService.getStatusLabel(invoice_info?.status)}
                                            color={getStatusColor(invoice_info?.status)}
                                            size="small"
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Shipments:
                                        </Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {invoice_info?.total_shipments}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Reverse Charge:
                                        </Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {invoice_info?.reverse_charge === 'yes' ? 'Yes' : 'No'}
                                        </Typography>
                                    </Box>
                                    
                                    {/* Payment Details - Show only if invoice is paid */}
                                    {invoice_info?.status === 'paid' && invoice_info?.paid_date && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle2" color="success.main" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                Payment Details
                                            </Typography>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Payment Date:
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {formatDate(invoice_info.paid_date)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Payment Mode:
                                                </Typography>
                                                <Chip
                                                    label={invoice_info.payment_mode}
                                                    color="success"
                                                    size="small"
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Reference No:
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {invoice_info.payment_reference_no}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Bill To
                                </Typography>
                                <Box sx={{ '& p': { mb: 0.5 } }}>
                                    <Typography 
                                        variant="body2" 
                                        dangerouslySetInnerHTML={{ 
                                            __html: customer_info?.customer_data_inv || 'Customer information not available' 
                                        }} 
                                    />
                                    {(() => {
                                        const addressHtml = customer_info?.customer_data_inv || '';
                                        const containsGST = /gst\s*:?/i.test(addressHtml);
                                        return !containsGST && customer_info?.billing_customer?.gst;
                                    })() && (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            GSTIN: {customer_info.billing_customer.gst}
                                        </Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Payment Mode Summary */}
                {summary_data && Object.keys(summary_data).length > 0 && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Payment Mode Summary
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Payment Mode</TableCell>
                                            <TableCell align="center">Shipments</TableCell>
                                            <TableCell align="right">Freight</TableCell>
                                                <TableCell align="right">Discount</TableCell>
                                                <TableCell align="right">Fuel Surcharge</TableCell>
                                            <TableCell align="right">Other Charges</TableCell>
                                            <TableCell align="right">Service Tax</TableCell>
                                            <TableCell align="right">Total Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(summary_data).map(([mode, data]) => (
                                            <TableRow key={mode}>
                                                <TableCell>
                                                    <Chip label={data.title} size="small" color="info" />
                                                </TableCell>
                                                <TableCell align="center">{data.shipmentCount}</TableCell>
                                                <TableCell align="right">{formatCurrency(data.totalFreight)}</TableCell>
                                                <TableCell align="right">{formatCurrency(data.discount || 0)}</TableCell>
                                                <TableCell align="right">{formatCurrency(data.fuel_surcharge)}</TableCell>
                                                <TableCell align="right">{formatCurrency(data.other_charges)}</TableCell>
                                                <TableCell align="right">{formatCurrency(data.totalService)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                    {formatCurrency(data.totalAmount)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}

                {/* Booking Details */}
                {booking_details && Object.keys(booking_details).length > 0 && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Shipment Details
                            </Typography>
                            {Object.entries(booking_details).map(([paymentMode, bookings]) => (
                                <Box key={paymentMode} sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip label={paymentMode} size="small" color="primary" />
                                        ({bookings.length} shipments)
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>AWB No. / Ref No.</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>From - To</TableCell>
                                                    <TableCell align="right">Freight</TableCell>
                                                    <TableCell align="right">Discount</TableCell>
                                                    <TableCell align="right">Fuel Surcharge</TableCell>
                                                    <TableCell align="right">Other Charges</TableCell>
                                                    <TableCell align="right">Service Tax</TableCell>
                                                    <TableCell align="right">Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {bookings.map((item, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {item.awb_no}
                                                            </Typography>
                                                            {item.booking_data?.sa_no && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Ref: {item.booking_data.sa_no}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {formatDate(item.booking_data?.booking_date)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {item.booking_data?.from_city?.city_name} - {item.booking_data?.to_city?.city_name}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Box>
                                                                <Typography variant="body2">
                                                                    {formatCurrency(item.charges?.basic_freight)}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {(() => {
                                                                const original = parseFloat(item.charges?.basic_freight) || 0;
                                                                const discountAmount = parseFloat(item.charges?.discount_amount) || 0;
                                                                const hasDiscount = discountAmount > 0;
                                                                const pct = hasDiscount && original > 0 ? ((discountAmount / original) * 100) : 0;
                                                                return hasDiscount ? (
                                                                    <Box>
                                                                        <Typography variant="body2">
                                                                            {formatCurrency(discountAmount)}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                                                            ({pct.toFixed(2)}%)
                                                                        </Typography>
                                                                    </Box>
                                                                ) : (
                                                                    <Typography variant="body2">-</Typography>
                                                                );
                                                            })()}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatCurrency(item.charges?.fuel_surcharge)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Box>
                                                                <Typography variant="body2">
                                                                    {formatCurrency(
                                                                        (parseFloat(item.charges?.handling_charges) || 0) +
                                                                        (parseFloat(item.charges?.stat_charges) || 0) +
                                                                        (parseFloat(item.charges?.to_pay_surcharge) || 0) +
                                                                        (parseFloat(item.charges?.cod_charges) || 0) +
                                                                        (parseFloat(item.charges?.sps_charges) || 0) +
                                                                        (parseFloat(item.charges?.value_surc) || 0) +
                                                                        (parseFloat(item.charges?.cr_charges) || 0) +
                                                                        (parseFloat(item.charges?.remote_area_charges) || 0)
                                                                    )}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatCurrency(item.charges?.ser_tax)}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                            {formatCurrency(item.charges?.grand_total)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                )}

                {/* Grand Total */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">
                                Grand Total
                            </Typography>
                            <Typography variant="h4" color="primary" fontWeight="bold">
                                {formatCurrency(Math.round(invoice_info?.grand_total || 0))}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                {/* Terms and Conditions */}
                {company_info?.terms_and_conditions && (
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Terms and Conditions
                            </Typography>
                            <Stack spacing={1}>
                                {company_info.terms_and_conditions.map((term, index) => (
                                    <Typography key={index} variant="body2" color="text.secondary">
                                        {index + 1}. {term}
                                    </Typography>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                )}
            </Box>

            {/* Email compose dialog (employee only), mounted only once opened */}
            {userType !== 'customer' && emailDialog.open && (
                <Suspense fallback={null}>
                    <EmailInvoiceDialog
                        open={emailDialog.open}
                        type={emailDialog.type}
                        invoiceId={invoiceId}
                        invoiceDetails={invoiceDetails}
                        onClose={handleCloseEmailDialog}
                    />
                </Suspense>
            )}
        </Box>
    );

    return isStandalonePage ? (
        <DashboardLayout userType={userType}>
            {content}
        </DashboardLayout>
    ) : content;
};

export default InvoiceDetails; 